import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  GraduationCap,
  Building2,
  Users,
  Briefcase,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  BarChart,
  FileText,
  Quote,
  Zap,
  Clock,
  Rocket,
  BookOpen,
  Menu,
  Plus,
  MessageSquare,
  Star,
} from "lucide-react";
import { postCampusConnect } from "../../network/APIs/campus";
import SuccessModal from "../contact-us/SuccessModel";
import { getTestimonals } from "../../network/APIs/materials";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
// import required modules
import { Pagination, Navigation } from "swiper/modules";
import collegeprofileplaceholder from "../../assets/images/college_profile_placeholder2.png";

const EduConnectPage = () => {
  const [campusSubmitBtn, setCampusSubmitBtn] = useState("Submit Request");
  const [isLoading, setIsLoading] = useState(false);
  const campusConnectFormRef = useRef(null);
  const swiperRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [collegeError, setCollegeError] = useState("");

  const onStartFreeTrial = () => {
    // document
    //   .getElementById("request-demo")
    //   ?.scrollIntoView({ behavior: "smooth" });
    // window.open("https://forms.gle/RdFKEBEKofCbTX2L6", "_blank");
    window.open("https://forms.gle/3qXjQrKAwjVFv6Pk7", "_blank");
  };

  useEffect(() => {
    getTestimonals().then((response) => {
      // console.log(response);
      setTestimonials(response?.data);
    });
  }, []);

  const campusConnectFormHandler = (e) => {
    e.preventDefault();

    const name = campusConnectFormRef.current.name.value || "";
    const email = campusConnectFormRef.current.email.value || "";
    const contact = campusConnectFormRef.current.contact.value || "";
    const institute = campusConnectFormRef.current.institute.value || "";
    const designation = campusConnectFormRef.current.designation.value || "";

    // Validate name field
    const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(name);
    const hasSpecialChars = /[^a-zA-Z\s]/.test(name);
    if (hasHtmlTags || hasSpecialChars) {
      setNameError("Name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
      return;
    }
    setNameError("");

    // Validate college name field
    const collegeHasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(institute);
    const collegeHasSpecialChars = /[^a-zA-Z\s]/.test(institute);
    if (collegeHasHtmlTags || collegeHasSpecialChars) {
      setCollegeError("College name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
      return;
    }
    setCollegeError("");

    // Validate phone field
    if (contact.length !== 10) {
      setPhoneError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^[6-9]/.test(contact)) {
      setPhoneError("Mobile number must start with 6, 7, 8, or 9.");
      return;
    }
    if (/^(\d)\1{9}$/.test(contact)) {
      setPhoneError("Invalid mobile number: repeated digits are not allowed.");
      return;
    }
    setPhoneError("");

    const prepareData = {
      name: name,
      email: email,
      contact_no: contact,
      institute_name: institute,
      designation: designation,
      no_of_students: 1,
    };
    // console.log(prepareData);
    if (!isLoading) {
      setIsLoading(true);
      postCampusConnect(prepareData)
        .then((response) => {
          setIsLoading(false);
          // console.log(".then", response);
          if (response.success) {
            campusConnectFormRef.current.reset();
            setShowPopup(true);
            setTimeout(() => setShowPopup(false), 4000);
            setCampusSubmitBtn("Response submitted!");
            setTimeout(() => {
              setCampusSubmitBtn("Submit Request");
            }, 2000);
          } else {
            console.log("error", response);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  const steps = [
    {
      step: "01",
      title: "College Registration",
      desc: "Register your institution and set up your placement cell profile in minutes.",
      color: "bg-orange-50 text-[#FF5F14]",
    },
    {
      step: "02",
      title: "Student Onboarding",
      desc: "Bulk upload student data or invite them to create verified profiles.",
      color: "bg-blue-50 text-blue-700",
    },
    {
      step: "03",
      title: "Connect with Companies",
      desc: "Gain visibility to 5,000+ companies hiring on Talent Connect.",
      color: "bg-purple-50 text-purple-700",
    },
    {
      step: "04",
      title: "Track & Close Placements",
      desc: "Manage drives, track offers, and generate detailed placement reports.",
      color: "bg-green-50 text-green-700",
    },
  ];

  const features = [
    {
      title: "300+ HR Contacts/Leads",
      desc: "Get fresh HR leads every month to boost your placement outreach.",
      icon: <Users className="text-[#FF5F14]" />,
    },
    {
      title: "Company & Job Visibility",
      desc: "Showcase your college to top recruiters actively hiring freshers.",
      icon: <Building2 className="text-[#FF5F14]" />,
    },
    {
      title: "Student Onboarding & Tracking",
      desc: "Seamlessly manage student profiles, resumes, and verification status.",
      icon: <BarChart className="text-[#FF5F14]" />,
    },
    {
      title: "Job Offers Management",
      desc: "Centralized dashboard to track all job openings and student applications.",
      icon: <Briefcase className="text-[#FF5F14]" />,
    },
    {
      title: "Analytics & Placement Reports",
      desc: "Generate comprehensive reports for NAAC/NBA accreditation.",
      icon: <FileText className="text-[#FF5F14]" />,
    },
    {
      title: "Secure & Verified Access",
      desc: "Role-based access control for placement officers and student coordinators.",
      icon: <ShieldCheck className="text-[#FF5F14]" />,
    },
  ];

  // const testimonials = [
  //   {
  //     quote:
  //       "EduConnectPro has completely transformed our placement process. The quality of HR leads we receive monthly is outstanding.",
  //     author: "Dr. Rajesh Kumar",
  //     role: "Placement Officer",
  //     college: "Sardar Patel Institute of Technology",
  //   },
  //   {
  //     quote:
  //       "Managing 500+ student profiles was a nightmare before. Now it's just a few clicks. Highly recommended for Tier-2 colleges.",
  //     author: "Prof. Anita Desai",
  //     role: "Director of Corporate Relations",
  //     college: "Modern Engineering College",
  //   },
  //   {
  //     quote:
  //       "The interface is intuitive, and the support team is very responsive. It feels like having an extended team working for our placements.",
  //     author: "Mr. Amit Shah",
  //     role: "TPO",
  //     college: "RK University",
  //   },
  // ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Thank You Popup */}
      <SuccessModal
        show={showPopup}
        onClose={() => setShowPopup(false)}
        title="Thank You!"
        message={`Thanks for reaching out!\nWe'll get back to you shortly.`}
        buttonLabel="Got it!"
      />

      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-[#FF5F14] font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-[#FF5F14]" />
            <span className="font-bold text-xl tracking-tight">
              EduConnect<span className="text-[#FF5F14]">Pro</span>
            </span>
          </div>
          <button
            onClick={onStartFreeTrial}
            className="flex xsm:hidden bg-[#FF5F14] text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-[#FF5F14]/90 transition shadow-lg shadow-[#FF5F14]/20"
          >
            Request Demo
          </button>
        </div>
      </div>

      {/* 1) HERO SECTION */}
      <div className="bg-white pt-28 xl:pt-20 pb-32 xl:pb-20 relative overflow-hidden">
        {/* Subtle background blob */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-orange-50/50 -skew-x-12 transform origin-top-right -z-10"></div>

        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 relative z-10">
          <div className="grid grid-cols-2 xl:grid-cols-1 gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 text-[#FF5F14] text-xs font-bold mb-6 tracking-wide uppercase">
                <span className="w-2 h-2 rounded-full bg-[#FF5F14] mr-2"></span>
                For Colleges & Institutions
              </div>
              <h1 className="text-6xl xl:text-5xl font-black text-gray-900 tracking-tight mb-6 leading-tight">
                Supercharge Your <br />
                <span className="text-[#FF5F14]">Placement Cell.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                Empower your institution with EduConnectPro. Connect students,
                companies, and opportunities on a single unified platform.
              </p>
              <div className="flex flex-row xsm:flex-col gap-4">
                <button
                  onClick={onStartFreeTrial}
                  className="bg-[#FF5F14] hover:bg-[#e04f0d] text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                >
                  Start Free Trial <ArrowRight size={20} />
                </button>
                <button
                  onClick={onStartFreeTrial}
                  className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold py-4 px-8 rounded-xl transition-colors"
                >
                  Contact Sales
                </button>
              </div>
              <p className="mt-4 text-sm text-gray-500 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-500" /> 14-Day
                Free Trial
                <span className="text-gray-300">|</span>
                <CheckCircle2 size={16} className="text-green-500" /> No Credit
                Card Required
              </p>
            </div>

            {/* Abstract Hero Image Representation */}
            <div className="relative h-[500px] xl:h-auto flex items-center justify-center">
              <div className="relative w-full max-w-md aspect-square bg-gradient-to-br from-orange-100 to-white rounded-full flex items-center justify-center shadow-2xl shadow-orange-500/10 border border-white">
                <div className="absolute inset-0 rounded-full border border-orange-200 border-dashed animate-[spin_10s_linear_infinite]"></div>
                <div className="text-center p-8">
                  <Briefcase
                    size={64}
                    className="text-[#FF5F14] mx-auto mb-4"
                  />
                  <h3 className="text-2xl font-bold text-gray-900">
                    300+ Leads
                  </h3>
                  <p className="text-gray-500">Delivered Monthly</p>
                </div>

                {/* Floating elements */}
                <div className="absolute top-10 -right-4 bg-white p-4 rounded-xl shadow-lg border-l-4 border-[#FF5F14]">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <CheckCircle2 size={20} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase">
                        Placement Rate
                      </p>
                      <p className="font-bold text-gray-900 text-lg">94%</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-10 -left-4 bg-white p-4 rounded-xl shadow-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Users size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase">
                        Active Students
                      </p>
                      <p className="font-bold text-gray-900 text-lg">1,250+</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2) FEATURES GRID - IMPROVED UI */}
      <div className="py-24 bg-gray-50 relative overflow-hidden">
        {/* Decorative background blobs */}
        <div className="absolute top-0 left-0 w-full h-full opacity-40 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-100 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-50 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#FF5F14] font-extrabold tracking-widest uppercase text-base bg-orange-50 px-4 py-1 rounded-full mb-4 inline-block">
              Why EduConnectPro?
            </span>
            <h2 className="text-6xl xsm:text-5xl font-black text-gray-900 mt-2 mb-6 tracking-tight">
              The Ultimate Upgrade for <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF5F14] to-orange-600">
                Placement Success.
              </span>
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              A comprehensive suite of tools designed specifically to modernize
              placement cells and improve student outcomes.
            </p>
          </div>

          <div className="grid grid-cols-3 xl:grid-cols-2 xsm:grid-cols-1 gap-8">
            {features.map((f, i) => (
              <div
                key={i}
                className="group relative bg-white p-8 rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-orange-500/10 hover:border-orange-100 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1.5 h-full bg-[#FF5F14] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-[#FF5F14]">{f.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#FF5F14] transition-colors">
                    {f.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2.5) STUDENT BENEFITS SECTION */}
      <div className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="grid grid-cols-2 xl:grid-cols-1 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <span className="text-blue-600 font-bold tracking-wide uppercase text-sm mb-2 block">
                Student Experience
              </span>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-6">
                What's in it for your{" "}
                <span className="text-blue-600">Students?</span>
              </h2>
              <p className="text-lg text-gray-600 mb-10">
                It's not just a tool for the TPO. It's a career companion for
                every student on campus.
              </p>

              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="mt-1 bg-blue-50 p-3 rounded-xl h-fit">
                    <Zap className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">
                      Direct Job Updates
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Students see job updates shared by the college immediately
                      on their mobile app.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="mt-1 bg-blue-50 p-3 rounded-xl h-fit">
                    <Clock className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">
                      Faster Drive Response
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Streamlined application process allows students to reply
                      to Campus Drives within 48 hours.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="mt-1 bg-blue-50 p-3 rounded-xl h-fit">
                    <Rocket className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">
                      Startup Grants
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Exclusive access to explore and apply for government and
                      private startup grants.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="mt-1 bg-blue-50 p-3 rounded-xl h-fit">
                    <BookOpen className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">
                      Interview Materials
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Access to a rich library of interview preparation
                      materials and past papers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Mobile Mockup */}
            <div className="relative mx-auto border-slate-800 bg-slate-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl flex flex-col justify-start overflow-hidden transform hover:scale-105 transition-transform duration-500">
              <div className="h-[32px] w-[3px] bg-slate-800 absolute -start-[17px] top-[72px] rounded-s-lg"></div>
              <div className="h-[46px] w-[3px] bg-slate-800 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
              <div className="h-[46px] w-[3px] bg-slate-800 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
              <div className="h-[64px] w-[3px] bg-slate-800 absolute -end-[17px] top-[142px] rounded-e-lg"></div>
              <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white relative">
                {/* Mockup Header */}
                <div className="bg-blue-600 p-4 pt-8 text-white">
                  <div className="flex justify-between items-center mb-4">
                    <Menu size={20} />
                    <span className="font-bold">Freshment</span>
                    <Users size={20} />
                  </div>
                  <div className="text-xs opacity-80 uppercase tracking-wider">
                    Placement Cell Updates
                  </div>
                  <div className="font-bold text-lg">College Feed</div>
                </div>

                {/* Mockup Body - Feed */}
                <div className="p-4 space-y-4 bg-gray-50 h-full">
                  {/* Job Card 1 */}
                  <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-2">
                      <div className="bg-orange-100 text-[#FF5F14] text-[10px] font-bold px-2 py-0.5 rounded">
                        New Drive
                      </div>
                      <span className="text-[10px] text-gray-400">2h ago</span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Software Engineer Trainee
                    </h4>
                    <p className="text-xs text-gray-500">
                      TechCorp Solutions • Bangalore
                    </p>
                    <div className="mt-2 flex gap-2">
                      <span className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-600">
                        ₹6-8 LPA
                      </span>
                      <span className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-600">
                        B.Tech
                      </span>
                    </div>
                    <button className="w-full mt-3 bg-blue-600 text-white text-xs font-bold py-2 rounded-lg">
                      Apply Now
                    </button>
                  </div>

                  {/* Job Card 2 */}
                  <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 opacity-90">
                    <div className="flex justify-between items-start mb-2">
                      <div className="bg-purple-100 text-purple-600 text-[10px] font-bold px-2 py-0.5 rounded">
                        Grant Info
                      </div>
                      <span className="text-[10px] text-gray-400">5h ago</span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Startup India Seed Fund
                    </h4>
                    <p className="text-xs text-gray-500">Government of India</p>
                    <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">
                      Applications open for student startups. Get up to ₹20
                      Lakhs funding...
                    </p>
                  </div>

                  {/* Floating Button */}
                  <div className="absolute bottom-4 right-4 bg-blue-600 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg z-10">
                    <Plus size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3) WORKFLOW */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Placements Made Simple
            </h2>
            <p className="text-gray-500 mt-2">
              From onboarding to offer letter in 4 steps
            </p>
          </div>

          <div className="grid grid-cols-4 xsm:grid-cols-1 gap-6 relative">
            <div className="block xsm:hidden absolute top-8 left-0 w-full h-0.5 bg-gray-100 -z-10" />
            {steps.map((item, idx) => (
              <div
                key={idx}
                className="relative bg-white p-6 rounded-2xl border border-gray-200 hover:border-orange-200 shadow-sm hover:shadow-md transition-all"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-6 ${item.color}`}
                >
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4) PRICING - COOL UI */}
      <div
        id="edu-pricing"
        className="py-24 bg-slate-900 relative overflow-hidden"
      >
        {/* Background Decor */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-[#FF5F14] rounded-full blur-[128px] opacity-20"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-blue-600 rounded-full blur-[128px] opacity-20"></div>

        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl xsm:text-3xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-400 text-lg">
              Everything you need to supercharge your placements.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border-4 border-[#FF5F14]/20 transform hover:scale-105 transition-transform duration-300">
              <div className="bg-[#FF5F14] p-2 text-center">
                <p className="text-white text-xs font-bold uppercase tracking-widest">
                  Most Popular for Colleges
                </p>
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Placement Accelerator
                </h3>
                <div className="flex justify-center items-baseline gap-1 my-6">
                  <span className="text-5xl font-extrabold text-gray-900">
                    ₹15,000
                  </span>
                  <span className="text-gray-500 font-medium">/ 6 months</span>
                </div>

                <div className="flex justify-center mb-8">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-[#FF5F14] text-sm font-bold">
                    Up to 100 Students
                  </span>
                </div>

                <ul className="space-y-4 text-left mb-8 px-4 xsm:px-2">
                  <li className="flex items-start gap-3 text-gray-700 font-medium">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5 shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                    <span>300+ Verified HR Leads / Month</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 font-medium">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5 shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                    <span>ATS Resume Builder Live Session</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 font-medium">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5 shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                    <span>Connect HR to Schedule Off campus Drive</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 font-medium">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5 shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                    <span>Track Student Progress and Application Matrix</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700 font-medium">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5 shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                    <span>Centralized Placement Tracking Dashboard</span>
                  </li>
                </ul>

                <button
                  onClick={onStartFreeTrial}
                  className="w-full bg-[#FF5F14] hover:bg-[#e04f0d] text-white font-bold py-4 rounded-xl shadow-lg shadow-orange-500/30 transition-all text-lg flex items-center justify-center gap-2"
                >
                  Start Free Trial <ArrowRight size={20} />
                </button>
                <p className="text-xs text-gray-400 mt-4">
                  No credit card required • Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 5) TESTIMONIALS - NEW SECTION */}
      <div className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            Trusted by Institutions
          </h2>
          <p className="text-gray-500 mt-2">
            See what placement officers are saying
          </p>
        </div>

        <div className="px-2 max-w-7xl flex w-full items-center mx-auto my-0">
          {/* Custom Navigation Buttons */}
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={isBeginning}
            className={`mx-6 block self-center h-fit p-2 bg-[#fff] rounded-full shadow-md shadow-blue-200 z-10 transition-all ${isBeginning
              ? "opacity-30 cursor-not-allowed"
              : "opacity-100 hover:bg-gray-50 active:scale-90"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1.2em"
              height="1.2em"
              viewBox="0 0 24 24"
            >
              <g fill="none" fillRule="evenodd">
                <path d="M24 0v24H0V0zM12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"></path>
                <path
                  fill="currentColor"
                  d="M7.94 13.06a1.5 1.5 0 0 1 0-2.12l5.656-5.658a1.5 1.5 0 1 1 2.121 2.122L11.122 12l4.596 4.596a1.5 1.5 0 1 1-2.12 2.122l-5.66-5.658Z"
                ></path>
              </g>
            </svg>
          </button>

          <Swiper
            slidesPerView={1}
            spaceBetween={20}
            pagination={{
              clickable: true,
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            modules={[Pagination, Navigation]}
            className="mySwiper"
          >
            {testimonials?.map((t, i) => (
              <SwiperSlide key={t.id || i}>
                <div
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-100 relative h-full flex flex-col"
                >
                  <div className="text-[#FF5F14] mb-4">
                    <Quote className="opacity-40" size={32} />
                  </div>
                  <p className="text-gray-700 mb-6 italic text-sm leading-relaxed flex-grow">
                    "{t.testimonial_text}"
                  </p>
                  <div>
                    <p className="font-bold text-gray-900">
                      {t.reviewer_name}
                    </p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      {t.reviewer_position}
                    </p>
                    <p className="text-sm text-[#FF5F14] font-medium mt-1">
                      {t.college_name}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <button
            onClick={() => swiperRef.current?.slideNext()}
            disabled={isEnd}
            className={`mx-2 block self-center h-fit p-2 bg-[#fff] rounded-full shadow-md shadow-blue-200 z-10 transition-all ${isEnd
              ? "opacity-30 cursor-not-allowed"
              : "opacity-100 hover:bg-gray-50 active:scale-90"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1.2em"
              height="1.2em"
              viewBox="0 0 24 24"
            >
              <g fill="none" fillRule="evenodd">
                <path d="M24 0v24H0V0zM12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"></path>
                <path
                  fill="currentColor"
                  d="M16.06 10.94a1.5 1.5 0 0 1 0 2.12l-5.656 5.658a1.5 1.5 0 1 1-2.121-2.122L12.879 12L8.283 7.404a1.5 1.5 0 0 1 2.12-2.122l5.658 5.657Z"
                ></path>
              </g>
            </svg>
          </button>
        </div>
      </div>

      {/* 6) REQUEST DEMO FORM (Replaces Enterprise Solutions) */}
      <section
        className="py-20 bg-slate-50 border-t border-slate-100"
        id="request-demo"
      >
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="flex flex-row xl:flex-col items-start gap-16">
            {/* Content */}
            <div className="w-1/2 xl:w-full">
              <div className="inline-flex items-center gap-2 text-[#FF5F14] font-bold uppercase tracking-wider mb-4">
                <MessageSquare className="w-5 h-5" />
                Enterprise Solutions
              </div>
              <h2 className="text-4xl font-black text-slate-900 mb-6">
                Partner with Freshment
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Have more than 100 students? We offer custom enterprise plans
                with dedicated support, API access, and advanced integrations.
                Fill in the details below to schedule a walkthrough.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#FF5F14]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">
                      {/* Student Data Management */}
                      Real-Time Placement Opportunities
                    </h4>
                    <p className="text-slate-500 text-sm mt-1">
                      {/* Centralized database for all student profiles, resumes,
                      and academic records. */}
                      Access verified placement leads and connect directly with
                      HRs---helping colleges overcome traditional industry
                      barriers.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-[#FF5F14]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">
                      Company Visibility
                    </h4>
                    <p className="text-slate-500 text-sm mt-1">
                      Showcase your institute to 5000+ top recruiters actively
                      hiring.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-[#FF5F14]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">
                      Placement Analytics
                    </h4>
                    <p className="text-slate-500 text-sm mt-1">
                      Real-time dashboards to track placement percentage,
                      average package, and more.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-6 h-6 text-[#FF5F14]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">
                      Value-Added Career Support
                    </h4>
                    <p className="text-slate-500 text-sm mt-1">
                      Free access to curated courses, webinars, and ATS resume
                      building sessions through our dedicated support sessions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="w-1/2 xl:w-full">
              <div className="bg-white rounded-3xl p-10 xsm:p-8 shadow-2xl border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5F14]/10 rounded-bl-full -mr-10 -mt-10"></div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2 relative z-10">
                  Request a Free Demo
                </h3>
                <p className="text-slate-500 mb-8 relative z-10">
                  Get a callback from our sales team within 24 hours.
                </p>

                <form
                  className="space-y-5 relative z-10"
                  // onSubmit={(e) => {
                  //   e.preventDefault();
                  //   alert("Request sent successfully!");
                  // }}
                  ref={campusConnectFormRef}
                  onSubmit={campusConnectFormHandler}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase">
                        Your Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        minLength={3}
                        onChange={(e) => {
                          const value = e.target.value;
                          const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(value);
                          const hasSpecialChars = /[^a-zA-Z\s]/.test(value);
                          if (hasHtmlTags || hasSpecialChars) {
                            setNameError("Name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
                          } else {
                            setNameError("");
                          }
                        }}
                        className={`w-full p-3 bg-slate-50 border rounded-xl focus:outline-none font-medium transition-colors ${nameError ? "border-red-500 focus:border-red-500" : "border-slate-200 focus:border-[#FF5F14]"}`}
                        placeholder="Full Name"
                        autoComplete="off"
                      />
                      {nameError && (
                        <p className="text-xs text-red-500 font-medium">{nameError}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase">
                        Phone
                      </label>
                      <input
                        id="contact"
                        name="contact"
                        type="text"
                        minLength={10}
                        maxLength={10}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          e.target.value = value;

                          if (value.length === 0) {
                            setPhoneError("");
                            return;
                          }

                          if (value.length !== 10) {
                            setPhoneError("Please enter a valid 10-digit mobile number.");
                            return;
                          }

                          if (!/^[6-9]/.test(value)) {
                            setPhoneError("Mobile number must start with 6, 7, 8, or 9.");
                            return;
                          }

                          if (/^(\d)\1{9}$/.test(value)) {
                            setPhoneError("Invalid mobile number: repeated digits are not allowed.");
                            return;
                          }

                          setPhoneError("");
                        }}
                        autoComplete="off"
                        required
                        className={`w-full p-3 bg-slate-50 border rounded-xl focus:outline-none font-medium transition-colors ${phoneError ? "border-red-500 focus:border-red-500" : "border-slate-200 focus:border-[#FF5F14]"}`}
                        placeholder="Phone Number"
                      />
                      {phoneError && (
                        <p className="text-xs text-red-500 font-medium">{phoneError}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase">
                      Official Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      minLength={3}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#FF5F14] font-medium transition-colors"
                      placeholder="tpo@college.edu.in"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase">
                      College / Institute Name
                    </label>
                    <input
                      id="institute"
                      name="institute"
                      type="text"
                      minLength={3}
                      required
                      onChange={(e) => {
                        const value = e.target.value;
                        const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(value);
                        const hasSpecialChars = /[^a-zA-Z\s]/.test(value);
                        if (hasHtmlTags || hasSpecialChars) {
                          setCollegeError("College name can only contain letters and spaces. Special characters and HTML tags are not allowed.");
                        } else {
                          setCollegeError("");
                        }
                      }}
                      className={`w-full p-3 bg-slate-50 border rounded-xl focus:outline-none font-medium transition-colors ${collegeError ? "border-red-500 focus:border-red-500" : "border-slate-200 focus:border-[#FF5F14]"}`}
                      placeholder="Institute Name"
                      autoComplete="off"
                    />
                    {collegeError && (
                      <p className="text-xs text-red-500 font-medium">{collegeError}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase">
                      Designation
                    </label>
                    <select
                      id="designation"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#FF5F14] font-medium transition-colors appearance-none"
                    >
                      <option>Placement Officer (TPO)</option>
                      <option>Principal / Director</option>
                      <option>HOD / Faculty</option>
                      <option>Student Coordinator</option>
                    </select>
                  </div>

                  <button className="w-full py-4 bg-[#FF5F14] hover:bg-[#FF5F14]/90 text-white font-bold rounded-xl shadow-lg shadow-[#FF5F14]/20 transition-all transform active:scale-[0.98] mt-2 flex items-center justify-center gap-2">
                    {/* Submit Request  */}
                    {campusSubmitBtn}
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  <p className="text-xs text-center text-slate-400 mt-4">
                    No credit card required for demo request.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7) FINAL CTA */}
      <div className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 text-center">
          <h2 className="text-4xl xsm:text-3xl font-bold text-gray-900 mb-8">
            Ready to upgrade your placement cell?
          </h2>
          <div className="flex flex-row xsm:flex-col justify-center gap-4">
            <button
              onClick={onStartFreeTrial}
              className="bg-[#FF5F14] hover:bg-[#e04f0d] text-white font-bold py-4 px-10 rounded-xl shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-1 text-lg flex items-center justify-center gap-2"
            >
              Start Free Trial <ArrowRight size={20} />
            </button>
            <button
              onClick={onStartFreeTrial}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold py-4 px-10 rounded-xl transition-colors text-lg"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EduConnectPage;
